import { useEffect, useRef, useState, useCallback } from 'react'
import { useGLTF } from '@react-three/drei'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
// import * as dat from 'lil-gui'
import {
  availableInteractableObjectAtom,
  currentInteractionAtom,
  playerLocationAtom,
  lastBiancaLocationAtom,
  insideHouseAtom,
  biancaLoadedAtom,
  activeMobileDirectionsAtom,
  activeMobileSprintAtom,
  currentJobProjectAtom,
  biancaBeingAnimatedAtom,
  loadingAtom,
  gameStartedAtom,
  loadingScreenClosedAtom,
  boundingBoxesMapAtom,
  onTopOfObstacleAtom,
  showHelpersAtom,
  pickedApplesAtom,
  justPickedApplesAtom,
  FAST_DEVICE_DELTA,
} from '../../store/store'
import { useAtom } from 'jotai'
import {
  BiancaAnimationName,
  InteractionTypeEnum,
  SceneChild,
  PositionToSave,
} from '../../types/portfolio_types'
import {
  determineYawToPointTo,
  getYawInDegrees,
  resolvePenetration,
  shouldSpinYawClockwise,
} from '../../helpers/helperFunctions'
import { deepDispose } from '../../helpers/deepDispose'
import emitter from '../../helpers/MittEmitter'
import useFaceManager from '../../hooks/FaceManager'
import { interactable_objects } from '../../interactable_data'
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js'
import { getAppleData } from '../../store/localstorageData'

type MovementDirection =
  | 'forward'
  | 'backward'
  | 'leftward'
  | 'rightward'
  | 'backward_right'
  | 'forward_right'
  | 'backward_left'
  | 'forward_left'

const PRIMARY_IDLE_ANIMATION = 'bianca_idle'
const SECONDARY_IDLE_ANIMATION = 'bianca_looking_around'
const MID_JUMP_ANIMATION = 'bianca_mid_jump'

const animation_speeds: { [key: string]: number } = {
  bianca_run: 1.4,
  bianca_walk: 1.1,
}

const small_obstacles: { [key: string]: number } = {
  red_button: 0.4,
  barrier_stair: 0.1,
  barrier_stair001: 0.1,
  barrier_stair002: 0.1,
  barrier_stair003: 0.1,
  barrier_stair004: 0.1,
}

const small_obstacles_fast_device_offset = ['stair']

const BIANCA_ID = 'bianca_rig'
const MAILMAN_CAT_SENSOR = 'sensor_mailman_cat'
const JUMP_SPRING_SENSOR = 'sensor_jump_spring'
const JUMP_SPRING_ID = 'jump_spring'
const DEFAULT_JUMP_POWER = 9.5
const FRAME_GRAVITY = 4

const Bianca = () => {
  const bianca_model = useGLTF('/models/portfolio_forest/bianca.glb')
  const three = useThree()
  const { camera } = three
  const [sceneChild, setSceneChild] = useState<SceneChild>()
  const [sceneClone, setSceneClone] = useState<THREE.Object3D<THREE.Object3DEventMap>>()

  // Scene must be cloned first, so we can't use useAnimations because that one requires an initial value
  const [mixer, setMixer] = useState<THREE.AnimationMixer>()

  const bianca_face_material_ref = useRef<THREE.MeshStandardMaterial | null>(null)
  useFaceManager({
    id: 'bianca',
    face_name: 'bianca',
    default_face: 'bianca neutral',
    face_material: bianca_face_material_ref.current,
    min_filter: THREE.LinearFilter,
  })

  const [interactableObject, setInteractableObject] = useAtom(availableInteractableObjectAtom)
  const [currentInteractionObj, setCurrentInteractionObj] = useAtom(currentInteractionAtom)
  const [playerLocationObj, setPlayerLocationObj] = useAtom(playerLocationAtom)
  const [lastBiancaLocation, setLastBiancaLocation] = useAtom(lastBiancaLocationAtom)
  const [insideHouse] = useAtom(insideHouseAtom)
  const [biancaLoaded, setBiancaLoaded] = useAtom(biancaLoadedAtom)
  const [biancaBeingAnimated, setBiancaBeingAnimated] = useAtom(biancaBeingAnimatedAtom)
  const [currentJobProject] = useAtom(currentJobProjectAtom)
  const [loading] = useAtom(loadingAtom)
  const [gameStarted] = useAtom(gameStartedAtom)
  const [loadingScreenClosed] = useAtom(loadingScreenClosedAtom)

  const default_location_set = useRef(false)
  const bianca_body = useRef<THREE.Object3D | null>(null)
  const bianca_helper = useRef<THREE.Box3Helper | null>(null)
  const [activeMobileDirectionsObj] = useAtom(activeMobileDirectionsAtom)
  const [activeMobileSprint] = useAtom(activeMobileSprintAtom)
  const [currentAction, setCurrentAction] = useState<THREE.AnimationAction>()
  const lookingDirection = useRef<MovementDirection>('backward')
  const smoothed_camera_position = useRef(new THREE.Vector3(0.16, 14.44, 25.85))

  const smoothed_camera_target = useRef(new THREE.Vector3())
  const camera_position_ref = useRef(new THREE.Vector3())
  const camera_target_ref = useRef(new THREE.Vector3())
  const camera_target_ref_2 = useRef(new THREE.Vector3())
  const camera_target_position_ref = useRef(new THREE.Vector3())
  const camera_target_intermediate_point = useRef(new THREE.Vector3())
  const focus_on_interact_zoom = new THREE.Vector3(-1, -7, -13)
  const camera_target_ref_3 = useRef(new THREE.Vector3())
  const zoomed_in_camera_ref = useRef(new THREE.Vector3())

  const [onTopOfObstacle, setOnTopOfObstacle] = useAtom(onTopOfObstacleAtom)

  const obstacle_to_walk_over = useRef<{ obstacle_y: number; name: string } | null>(null)
  const [interactCameraPosition, setInteractCameraPosition] = useState<THREE.Vector3 | null>(null)
  const target_yaw = useRef<number | null>(null)
  const spin_to_face_interact_clockwise = useRef(false)
  const is_midair = useRef(false)
  const current_idle_animation = useRef<BiancaAnimationName>(PRIMARY_IDLE_ANIMATION)
  const secondary_idle_animation_timeout = useRef<null | NodeJS.Timeout>(null)
  const can_focus_on_interactable = useRef(false)

  const [bbMap] = useAtom(boundingBoxesMapAtom)
  const [showHelpers] = useAtom(showHelpersAtom)

  const [pickedApples, setPickedApples] = useAtom(pickedApplesAtom)
  const [justPickedApples, setJustPickedApples] = useAtom(justPickedApplesAtom)

  const frame_vel = useRef(new THREE.Vector3())
  const frame_y_vel = useRef(new THREE.Vector3())
  const collision_bbox = useRef(new THREE.Box3())
  const highest_midair_y = useRef(0)
  const just_jumped_on_spring = useRef(false)

  const kForward = useRef(false)
  const kBackward = useRef(false)
  const kLeftward = useRef(false)
  const kRightward = useRef(false)
  const kSprint = useRef(false)

  // ================================================================================================= //

  const changeAction = useCallback(
    (animation_name: BiancaAnimationName, play_once?: boolean) => {
      const currentClip = currentAction?.getClip()

      if (
        (!currentAction || (currentAction && currentClip && currentClip.name !== animation_name)) &&
        sceneClone &&
        mixer
      ) {
        const fadeDuration = 0.5
        currentAction?.fadeOut(fadeDuration)

        // It's necessary to always reset the animation when using fadeIn/fadeOut
        const animation_clip = sceneClone.animations.find((clip) => clip.name === animation_name)!
        const animation = mixer.clipAction(animation_clip).reset().fadeIn(fadeDuration).play()

        if (play_once) animation.loop = THREE.LoopOnce
        animation.timeScale = animation_speeds[animation_name] || 1

        setCurrentAction(animation)
      }
    },
    [currentAction, mixer, sceneClone],
  )

  const jump = (prepare_for_jump: boolean, power?: number) => {
    if (!bianca_body.current || !mixer || !sceneClone) return
    const jump_velocity = power || DEFAULT_JUMP_POWER

    // Animate jump
    setBiancaBeingAnimated('jump')

    if (prepare_for_jump) {
      const switchToMidJump = () => {
        // Non-looping animations (bianca_preparing_jump) don't fade well, so some tweaking was needed to make it look passable
        // Next time use only looping ones.
        const fadeDuration = 0.4
        currentAction!.fadeOut(0.1)

        const mid_jump_animation_clip = sceneClone.animations.find(
          (clip) => clip.name === MID_JUMP_ANIMATION,
        )!
        const mid_jump_animation_action = mixer
          .clipAction(mid_jump_animation_clip)
          .reset()
          .fadeIn(fadeDuration)
          .play()

        setCurrentAction(mid_jump_animation_action)
        frame_y_vel.current.y = jump_velocity
        mixer.removeEventListener('finished', switchToMidJump)
      }

      changeAction('bianca_preparing_jump', true)
      mixer.addEventListener('finished', switchToMidJump)
    } else {
      frame_y_vel.current.y = jump_velocity
    }
  }

  // 1) Clone the scene + Set default position and rotation
  // + play idle animation + set the face material to use in the face manager
  useEffect(() => {
    if (sceneClone) return

    const cloned_scene = SkeletonUtils.clone(bianca_model.scene)
    cloned_scene.animations = bianca_model.animations

    const mixer = new THREE.AnimationMixer(cloned_scene)
    const bianca_rig = cloned_scene.children.find((child) => child.name === 'bianca_rig')!

    bianca_rig.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        child.castShadow = true
      }
    })

    // The bbox that threejs generates for bianca_rig is a bit too large due to
    // her tail, so we're using a custom bbox object for that (adjusting it here was too much of a hassle)
    const bbox = cloned_scene.children.find((child) => child.name === 'bbox')!

    bianca_body.current = bbox

    const entered_house_position = new THREE.Vector3(0.5, 3, 12)
    const default_position = insideHouse ? entered_house_position : lastBiancaLocation.position
    bianca_body.current.position.copy(default_position)

    const default_rotation = insideHouse ? new THREE.Euler(0, Math.PI, 0, 'XYZ') : lastBiancaLocation.rotation
    bianca_body.current.rotation.copy(default_rotation)

    setSceneClone(cloned_scene)
    setSceneChild({
      object: bianca_rig,
      element: <primitive object={bianca_rig} key={bianca_rig.id} />,
    })
    setMixer(mixer)

    const idle_animation_clip = cloned_scene.animations.find((clip) => clip.name === PRIMARY_IDLE_ANIMATION)!
    const idle_animation_action = mixer.clipAction(idle_animation_clip).play()
    setCurrentAction(idle_animation_action)

    const bianca_face_material = bianca_model.materials.bianca_face as THREE.MeshStandardMaterial

    if (bianca_face_material.map) {
      bianca_face_material_ref.current = bianca_face_material
    }
  }, [bianca_model, sceneClone, insideHouse, lastBiancaLocation])

  // 2) Keep children in scene (in this case we could skip this but I'm doing it for consistency)
  useEffect(() => {
    if (!sceneChild || !sceneClone) return

    const child_is_in_scene = sceneClone.children.find((child) => child.name === sceneChild.object.name)
    if (!child_is_in_scene) sceneClone.children.push(sceneChild.object)
  }, [sceneChild, sceneClone])

  // 3) Set bianca loaded + helpers
  useEffect(() => {
    if (!default_location_set.current && bianca_body.current) {
      setTimeout(() => {
        can_focus_on_interactable.current = true
      }, 2800)

      setBiancaLoaded(true)
      default_location_set.current = true
      collision_bbox.current.setFromObject(bianca_body.current)

      if (showHelpers) {
        bianca_helper.current = new THREE.Box3Helper(collision_bbox.current, 0xff0000)
        three.scene.add(bianca_helper.current)
      }
    }
  })

  // 4) Listeners for movement
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()

      if (!kForward.current && (key === 'arrowup' || key === 'w')) {
        kForward.current = true
      } else if (!kLeftward.current && (key === 'arrowleft' || key === 'a')) {
        kLeftward.current = true
      } else if (!kBackward.current && (key === 'arrowdown' || key === 's')) {
        kBackward.current = true
      } else if (!kRightward.current && (key === 'arrowright' || key === 'd')) {
        kRightward.current = true
      } else if (!kSprint.current && key === 'shift') {
        kSprint.current = true
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase()

      if (key === 'arrowup' || key === 'w') {
        kForward.current = false
      } else if (key === 'arrowleft' || key === 'a') {
        kLeftward.current = false
      } else if (key === 'arrowdown' || key === 's') {
        kBackward.current = false
      } else if (key === 'arrowright' || key === 'd') {
        kRightward.current = false
      } else if (key === 'shift') {
        kSprint.current = false
      }
    }

    const handleWindowBlur = () => {
      kForward.current = false
      kLeftward.current = false
      kBackward.current = false
      kRightward.current = false
      kSprint.current = false
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)
    window.addEventListener('blur', handleWindowBlur)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [])

  // 5) dat.GUI
  // useEffect(() => {
  //   const newGui = new dat.GUI({ closeFolders: false })
  //   const obj = {
  //     checkBiancaYaw: () => {
  //       if (!bianca_body.current) return

  //       console.log(getYawInDegrees(bianca_body.current.quaternion))
  //       console.log(bianca_body.current.position)
  //       console.log(bianca_body.current.rotation)
  //     },
  //   }
  //   newGui.add(obj, 'checkBiancaYaw')
  // }, [])

  // 6) Trigger animations on interactions + Rotate towards objects
  useEffect(() => {
    if (!bianca_body.current) return

    if (!currentInteractionObj) {
      current_idle_animation.current = PRIMARY_IDLE_ANIMATION
      setPlayerLocationObj(null)

      return
    }

    const { currentInteraction, currentlyInspecting } = currentInteractionObj

    if (currentlyInspecting.id === BIANCA_ID) {
      currentlyInspecting.position = camera.position
    }

    // Manage current and target yaw
    if (!currentlyInspecting.position) {
      setPlayerLocationObj(null)
    } else if (currentInteraction.type === InteractionTypeEnum.dialog) {
      const current_yaw = getYawInDegrees(bianca_body.current.quaternion)
      const yawToTarget = determineYawToPointTo(currentlyInspecting.position, bianca_body.current.position)

      spin_to_face_interact_clockwise.current = shouldSpinYawClockwise(current_yaw, yawToTarget)
      target_yaw.current = yawToTarget
    }

    clearIdleAnimationTimeout()

    if (currentlyInspecting.on_inspect_animation) {
      current_idle_animation.current = currentlyInspecting.on_inspect_animation
    } else {
      current_idle_animation.current = PRIMARY_IDLE_ANIMATION
    }

    if (currentlyInspecting.id === JUMP_SPRING_SENSOR && !is_midair.current) {
      jump(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInteractionObj])

  // 7) Save last bianca position when we go into a world or inside the mushroom house
  // It must be emitted right before setting a job project and not on Bianca's unmount
  // so that we can set the default exit position on a project when moving with the
  // browser's navigation arrow keys
  useEffect(() => {
    const saveLastBiancaPosition = (save_to: PositionToSave) => {
      if (!bianca_body.current) return

      if (save_to === 'world') {
        setLastBiancaLocation({
          position: bianca_body.current.position,
          rotation: bianca_body.current.rotation,
        })
      } else {
        const door_position = interactable_objects.sensor_house_door.position
        if (!door_position) return

        const outside_house_rotation = new THREE.Euler()
        const outside_house_position = new THREE.Vector3(
          door_position.x,
          lastBiancaLocation.position.y,
          door_position.z + 5,
        )

        setLastBiancaLocation({
          position: outside_house_position,
          rotation: outside_house_rotation,
        })
      }
    }

    emitter.on('save_bianca_location', saveLastBiancaPosition)

    return () => {
      emitter.off('save_bianca_location')
    }
  }, [insideHouse, lastBiancaLocation, setBiancaLoaded, setLastBiancaLocation, currentJobProject])

  // 8) DISPOSE OBJECTS ON UNMOUNT
  useEffect(() => {
    return () => {
      if (!biancaLoaded || !sceneChild) return
      const helper = bianca_helper.current

      if (helper) {
        three.scene.remove(helper)
        if (helper.dispose) helper.dispose()
        bianca_helper.current = null
      }

      bianca_body.current = null
      default_location_set.current = false
      setBiancaLoaded(false)
      setSceneClone(undefined)
      setSceneChild(undefined)
      setPlayerLocationObj(null)
      deepDispose(sceneChild.object)
    }
  }, [sceneChild, biancaLoaded, setBiancaLoaded, setPlayerLocationObj, three.scene])

  const targetYawSatisfied = () => {
    if (!bianca_body.current || target_yaw.current === null) return true

    const t_yaw = target_yaw.current
    const current_yaw = getYawInDegrees(bianca_body.current.quaternion)
    const offset = 4
    const target_yaw_low = t_yaw - offset < 0 ? t_yaw - offset + 360 : t_yaw - offset
    const target_yaw_high = t_yaw - offset > 359 ? t_yaw + offset - 360 : t_yaw + offset

    const wrap_around = target_yaw_low > target_yaw_high

    if (
      (!wrap_around && current_yaw > target_yaw_low && current_yaw < target_yaw_high) ||
      (wrap_around && (current_yaw >= target_yaw_low || current_yaw <= target_yaw_high))
    ) {
      // Right after we finish rotating, let the interacting object he can rotate now if he is the cat
      if (!playerLocationObj && currentInteractionObj?.currentlyInspecting.id === MAILMAN_CAT_SENSOR) {
        setPlayerLocationObj({
          id: BIANCA_ID,
          world_position: bianca_body.current.position,
        })
      }

      target_yaw.current = null
      return true
    }

    return false
  }

  const cameraFollowMe = (camera: THREE.Camera) => {
    if (!bianca_body.current) return

    const body_position = bianca_body.current.position
    const camera_position = camera_position_ref.current.copy(body_position)
    const camera_target = camera_target_ref.current.copy(body_position)

    const body_approaching_world_end_z = insideHouse ? 2.5 : 30

    if (body_position.z > body_approaching_world_end_z) {
      camera_position.z = body_approaching_world_end_z
      camera_target.z = body_approaching_world_end_z
    }

    camera_position.y += 14.44
    camera_position.z += 25.85

    // Focus on interactable objects
    // loadingScreenClosed is different from loading because it's set to true after the
    // screen-closing animation plays. This prevents the camera from un-focusing right
    // after the loading is triggered when we confirm we want to go into a world.
    if (
      interactableObject?.position &&
      interactableObject.focus_on_contact &&
      !loadingScreenClosed &&
      can_focus_on_interactable.current
    ) {
      const camera_target = camera_target_ref_2.current.copy(interactableObject.position)
      smoothed_camera_target.current.lerp(camera_target, 0.05)

      // Some interactable objects that have a focus on contact also move the camera position a bit
      if (interactableObject.camera_position_offset) {
        const y_limit = 2.5
        const threshold = 0.01

        const target_position =
          interactCameraPosition ||
          camera_target_position_ref.current
            .copy(smoothed_camera_position.current)
            .add(interactableObject.camera_position_offset)

        if (target_position.y < y_limit) target_position.y = y_limit

        if (
          !interactCameraPosition ||
          smoothed_camera_position.current.distanceTo(target_position) > threshold
        ) {
          setInteractCameraPosition(target_position)
          smoothed_camera_position.current.lerp(target_position, 0.05)
          camera.position.copy(smoothed_camera_position.current)
        }
      }

      return camera.lookAt(smoothed_camera_target.current)
    }

    // Focus on the interactable object when an interaction with "focus on start" is triggered
    if (
      currentInteractionObj &&
      currentInteractionObj.currentInteraction.type === InteractionTypeEnum.dialog &&
      currentInteractionObj.currentInteraction.focus_on_start
    ) {
      const interacting_obj_position = currentInteractionObj.currentlyInspecting.position

      if (interacting_obj_position) {
        const intermediate_point = camera_target_intermediate_point.current
          .copy(bianca_body.current.position)
          .lerp(interacting_obj_position, 0.5)

        const camera_target = camera_target_ref_3.current.copy(intermediate_point)
        smoothed_camera_target.current.lerp(camera_target, 0.05)

        const zoomed_in_position =
          interactCameraPosition ||
          zoomed_in_camera_ref.current.copy(smoothed_camera_position.current).add(focus_on_interact_zoom)

        if (!interactCameraPosition) {
          setInteractCameraPosition(zoomed_in_position)
        }

        smoothed_camera_position.current.lerp(zoomed_in_position, 0.05)
        camera.position.copy(smoothed_camera_position.current)

        return camera.lookAt(smoothed_camera_target.current)
      }
    }

    if (!interactableObject && !currentInteractionObj && interactCameraPosition) {
      setInteractCameraPosition(null)
    }

    smoothed_camera_position.current.lerp(camera_position, 0.1)
    // Determines if Bianca will look shaky/jittery when moving fast.
    // Larger values = more jittery. Smaller values: less jittery but
    // the camera takes longer to face you, a good balance is under 0.3
    smoothed_camera_target.current.lerp(camera_target, 0.1)

    camera.position.copy(smoothed_camera_position.current)
    camera.lookAt(smoothed_camera_target.current)
  }

  const clearIdleAnimationTimeout = () => {
    if (secondary_idle_animation_timeout.current) {
      clearTimeout(secondary_idle_animation_timeout.current)
      secondary_idle_animation_timeout.current = null
    }
  }

  const slideDown = () => {
    if (!bianca_body.current) return
    setBiancaBeingAnimated('slide')
    changeAction('bianca_sliding_down')
  }

  const sensorFunctions: { [key: string]: () => void } = {
    sensor_slide: slideDown,
  }

  const pickApple = (apple_name: string) => {
    if (!pickedApples) return

    const apple_data = getAppleData()
    const updated_apples = { ...apple_data }

    if (updated_apples.list[apple_name] === false) {
      updated_apples.collected += 1
      updated_apples.list[apple_name] = true

      setPickedApples(updated_apples)
      setJustPickedApples({
        ...justPickedApples,
        [apple_name]: true,
      })
    }
  }

  const handleBiancaCollisions = (delta: number) => {
    if (!bianca_body.current || !biancaLoaded) return

    const bianca_bbox = collision_bbox.current.setFromObject(bianca_body.current)
    const pushingYUp: { name: string; offset_applied: number }[] = []

    // Add bianca to the bbMap so she can collide with balls
    if (bbMap) {
      bbMap.bianca = { bbox: bianca_bbox, name: 'bianca', type: 'collision' }
    }

    for (const bb_key in bbMap) {
      const { type, bbox, name } = bbMap[bb_key]
      const biancaIntersectsBox = bianca_bbox.intersectsBox(bbox)

      if (name === 'bianca') continue

      if (type === 'collision') {
        const res = resolvePenetration(bianca_bbox, bbox, bianca_body.current)

        if (res?.axis === 'y' && res.offset_applied > 0) {
          pushingYUp.push({ offset_applied: res.offset_applied, name })
        }

        if (
          biancaIntersectsBox &&
          small_obstacles[name] &&
          res?.axis !== 'y' &&
          obstacle_to_walk_over.current?.name !== name
        ) {
          const bianca_y = bianca_bbox.min.y
          const obstacle_y = bbox.max.y

          if (Number(bianca_y.toFixed(2)) < Number(obstacle_y.toFixed(2))) {
            obstacle_to_walk_over.current = { obstacle_y, name }
          }
        } else if (
          biancaIntersectsBox &&
          small_obstacles[name] &&
          res?.axis === 'y' &&
          !obstacle_to_walk_over.current &&
          !onTopOfObstacle
        ) {
          setOnTopOfObstacle(name)
        } else if (biancaIntersectsBox && onTopOfObstacle && onTopOfObstacle !== name && res?.axis === 'y') {
          setOnTopOfObstacle('')
        }
      } else if (type === 'sensor' && biancaIntersectsBox && interactable_objects[name]) {
        setInteractableObject(interactable_objects[name])
      } else if (type === 'sensor' && !biancaIntersectsBox && interactableObject?.id === name) {
        setInteractableObject(null)
      } else if (type === 'sensor' && biancaIntersectsBox && sensorFunctions[name]) {
        sensorFunctions[name]()
      } else if (type === 'sensor' && biancaIntersectsBox && name.includes('apple')) {
        pickApple(name)
      }
    }

    // Add jump power to the body (+y velocity)
    bianca_body.current.position.y += frame_y_vel.current.y * delta * (just_jumped_on_spring.current ? 2 : 1)

    // Decimate jump power with gravity
    if (frame_y_vel.current.y > 0) {
      frame_y_vel.current.y -= FRAME_GRAVITY * delta
    } else {
      frame_y_vel.current.y = 0
    }

    const bianca_y = bianca_body.current.position.y

    if (is_midair.current) {
      if (bianca_y > highest_midair_y.current) {
        highest_midair_y.current = bianca_y
      }
    }

    const new_midair_value = pushingYUp.length === 0

    // Logic for landing
    if (is_midair.current && !new_midair_value) {
      // End jump interaction when inspecting jump spring
      if (currentInteractionObj) {
        const { currentInteraction, currentlyInspecting } = currentInteractionObj

        if (
          currentInteraction.type === InteractionTypeEnum.animation &&
          currentlyInspecting.id === JUMP_SPRING_SENSOR
        ) {
          setCurrentInteractionObj(null)
        }
      }

      const landed_on_jump_spring = pushingYUp.find((item) => item.name === JUMP_SPRING_ID)
      just_jumped_on_spring.current = !!landed_on_jump_spring

      if (landed_on_jump_spring) {
        emitter.emit('play_animation', 'jump_spring_animation')
        jump(false, 9.2)
      } else if (biancaBeingAnimated !== 'slide') {
        // Add a little bounce if falling from a certain height
        const falling_diff = highest_midair_y.current - bianca_y
        const should_bounce = falling_diff > 2.7

        if (should_bounce) frame_y_vel.current.y = 5.5
        setBiancaBeingAnimated(should_bounce ? 'jump' : undefined)
        highest_midair_y.current = 0
      }

      // Also set bianca being jump animated when we just fall from somewhere without jumping
    } else if (new_midair_value && !biancaBeingAnimated) {
      setBiancaBeingAnimated('jump')
    }

    is_midair.current = new_midair_value

    // Move bianca
    bianca_body.current.position.add(frame_vel.current)
    frame_vel.current.set(0, 0, 0)
  }

  useFrame((state, delta) => {
    if (!bianca_body.current || !sceneChild) return
    sceneChild.object.position.copy(bianca_body.current.position)
    sceneChild.object.rotation.copy(bianca_body.current.rotation)

    // When switching tabs browsers will pause rendering, so delta
    // will jump to a value like +1.5. That'll cause false collision
    // bugs by pushing down with a lot of gravity force.
    // Animations play more naturally with just delta though.
    const safeDelta = Math.min(delta, 0.01)

    if (mixer) mixer.update(delta)

    const fast_device = delta <= FAST_DEVICE_DELTA
    const faster_delta_for_gravity = safeDelta * 2.7 * (fast_device ? 1 : 1.6)
    bianca_body.current.position.y -= FRAME_GRAVITY * faster_delta_for_gravity
    handleBiancaCollisions(faster_delta_for_gravity)

    if (is_midair.current && !biancaBeingAnimated) {
      changeAction(MID_JUMP_ANIMATION)
    } else if (loading || !gameStarted) {
      changeAction(current_idle_animation.current)
    }

    if (loading || !gameStarted) {
      collision_bbox.current.setFromObject(bianca_body.current)
      return cameraFollowMe(state.camera)
    }

    const { mForward, mBackward, mLeftward, mRightward } = activeMobileDirectionsObj

    const forward = kForward.current || mForward
    const backward = kBackward.current || mBackward
    const leftward = kLeftward.current || mLeftward
    const rightward = kRightward.current || mRightward
    const sprint = kSprint.current || activeMobileSprint

    const no_keys_pressed = !forward && !backward && !leftward && !rightward
    const in_dialog = currentInteractionObj?.currentInteraction.type === InteractionTypeEnum.dialog

    if (
      (no_keys_pressed || (backward && forward) || (leftward && rightward) || in_dialog) &&
      !biancaBeingAnimated
    ) {
      if (target_yaw.current && !targetYawSatisfied() && document.hasFocus()) {
        changeAction('bianca_walk')
        const spin_power = 2

        const spin_direction = spin_to_face_interact_clockwise.current ? -spin_power : spin_power
        const spin_y = spin_direction * safeDelta
        bianca_body.current.rotation.y += spin_y
      } else {
        if (
          !secondary_idle_animation_timeout.current &&
          current_idle_animation.current !== SECONDARY_IDLE_ANIMATION &&
          !currentInteractionObj?.currentlyInspecting.on_inspect_animation &&
          !in_dialog
        ) {
          secondary_idle_animation_timeout.current = setTimeout(() => {
            current_idle_animation.current = SECONDARY_IDLE_ANIMATION
          }, 6000)
        }

        if (!is_midair.current) {
          changeAction(current_idle_animation.current)
        }
      }

      collision_bbox.current.setFromObject(bianca_body.current)

      return cameraFollowMe(state.camera)
    }

    clearIdleAnimationTimeout()
    current_idle_animation.current = PRIMARY_IDLE_ANIMATION

    let vel_strength = sprint ? 18 * safeDelta : 13 * safeDelta

    if (is_midair.current) vel_strength = sprint ? 9 * safeDelta : 6 * safeDelta
    if (biancaBeingAnimated === 'slide') vel_strength = 11 * safeDelta

    const torque_strength = 10 * safeDelta
    const yaw_direction = getYawInDegrees(bianca_body.current.quaternion)

    const looking_forward = yaw_direction >= 177 && yaw_direction <= 183
    const looking_backward = yaw_direction >= 357 || yaw_direction <= 3
    const looking_leftward = yaw_direction >= 265 && yaw_direction <= 275
    const looking_rightward = yaw_direction >= 85 && yaw_direction <= 95

    const looking_backward_right = yaw_direction >= 40 && yaw_direction <= 50
    const looking_forward_right = yaw_direction >= 130 && yaw_direction <= 140
    const looking_backward_left = yaw_direction >= 310 && yaw_direction <= 320
    const looking_forward_left = yaw_direction >= 220 && yaw_direction <= 230

    if (looking_forward) lookingDirection.current = 'forward'
    if (looking_backward) lookingDirection.current = 'backward'
    if (looking_leftward) lookingDirection.current = 'leftward'
    if (looking_rightward) lookingDirection.current = 'rightward'
    if (looking_backward_right) lookingDirection.current = 'backward_right'
    if (looking_forward_right) lookingDirection.current = 'forward_right'
    if (looking_backward_left) lookingDirection.current = 'backward_left'
    if (looking_forward_left) lookingDirection.current = 'forward_left'

    if (obstacle_to_walk_over.current) {
      const { obstacle_y, name } = obstacle_to_walk_over.current
      let fast_device_offset = 1

      if (
        !fast_device &&
        small_obstacles_fast_device_offset.find((obstacle_name) => name.includes(obstacle_name))
      ) {
        fast_device_offset = 1.6
      }

      // Offset a little of the y position so the jump doesn't look so abrupt
      bianca_body.current.position.y += obstacle_y * small_obstacles[name] * fast_device_offset
      obstacle_to_walk_over.current = null
    }

    if (forward && (leftward || rightward)) {
      frame_vel.current.z -= vel_strength / 1.2
    } else if (forward) {
      frame_vel.current.z -= vel_strength
    }

    if (backward && (leftward || rightward)) {
      frame_vel.current.z += vel_strength / 1.2
    } else if (backward) {
      frame_vel.current.z += vel_strength
    }

    if (leftward && (forward || backward)) {
      frame_vel.current.x -= vel_strength / 1.2
    } else if (leftward) {
      frame_vel.current.x -= vel_strength
    }

    if (rightward && (forward || backward)) {
      frame_vel.current.x += vel_strength / 1.2
    } else if (rightward) {
      frame_vel.current.x += vel_strength
    }

    if (biancaBeingAnimated === 'slide') {
      frame_vel.current.x = 0
      frame_vel.current.x -= vel_strength * (fast_device ? 1 : 1.7)
      const finish_sliding = -4.5

      if (bianca_body.current.position.x < finish_sliding) {
        setBiancaBeingAnimated(undefined)
      }
    }

    // Negative torque: clockwise rotation (left) - Positive: counterclockwise rotation (right)
    const directions = [
      {
        direction_buttons: forward && rightward,
        direction_active: looking_forward_right,
        clockwise_directions: ['backward_left', 'leftward', 'forward_left', 'forward'],
      },
      {
        direction_buttons: forward && leftward,
        direction_active: looking_forward_left,
        clockwise_directions: ['backward', 'backward_left', 'leftward'],
      },
      {
        direction_buttons: backward && rightward,
        direction_active: looking_backward_right,
        clockwise_directions: ['forward_left', 'forward', 'forward_right', 'rightward'],
      },
      {
        direction_buttons: backward && leftward,
        direction_active: looking_backward_left,
        clockwise_directions: ['backward', 'backward_right', 'rightward', 'forward_right'],
      },
      {
        direction_buttons: forward,
        direction_active: looking_forward,
        clockwise_directions: ['backward', 'backward_left', 'leftward', 'forward_left'],
      },
      {
        direction_buttons: backward,
        direction_active: looking_backward,
        clockwise_directions: ['forward', 'forward_right', 'rightward', 'backward_right'],
      },
      {
        direction_buttons: leftward,
        direction_active: looking_leftward,
        clockwise_directions: ['rightward', 'backward_right', 'backward', 'backward_left'],
      },
      {
        direction_buttons: rightward,
        direction_active: looking_rightward,
        clockwise_directions: ['leftward', 'forward_left', 'forward', 'forward_right'],
      },
    ]

    for (const dir of directions) {
      if (dir.direction_buttons) {
        if (!dir.direction_active) {
          const rotateClockwise = dir.clockwise_directions.includes(lookingDirection.current)
          bianca_body.current.rotation.y += rotateClockwise ? -torque_strength : torque_strength
        }

        break
      }
    }

    if (!biancaBeingAnimated && !is_midair.current) {
      if (sprint) {
        changeAction('bianca_run')
      } else {
        changeAction('bianca_walk')
      }
    }

    collision_bbox.current.setFromObject(bianca_body.current)

    // CAMERA
    cameraFollowMe(state.camera)
    return
  })

  if (!sceneChild) return null

  return sceneChild.element
}

export default Bianca

useGLTF.preload('/models/portfolio_forest/bianca.glb')
